/**
 * 每日英语数据服务
 * 使用免费数据源提供英语句子
 */

import { isExtensionEnvironment } from '../../../../utils/extensionUtils';

// 本地英语句子数据库（免费开源数据）
const localSentences = [
  {
    id: 'local_1',
    english: 'The future belongs to those who believe in the beauty of their dreams.',
    chinese: '未来属于那些相信梦想之美的人。',
    author: 'Eleanor Roosevelt',
    category: 'motivation',
    difficulty: 'intermediate',
    tags: ['梦想', '未来', '励志']
  },
  {
    id: 'local_2',
    english: 'Success is not final, failure is not fatal: it is the courage to continue that counts.',
    chinese: '成功不是终点，失败不是致命的：重要的是继续前进的勇气。',
    author: 'Winston Churchill',
    category: 'success',
    difficulty: 'intermediate',
    tags: ['成功', '失败', '勇气']
  },
  {
    id: 'local_3',
    english: 'The only way to do great work is to love what you do.',
    chinese: '做出伟大工作的唯一方法就是热爱你所做的事情。',
    author: 'Steve Jobs',
    category: 'work',
    difficulty: 'beginner',
    tags: ['工作', '热爱', '成功']
  },
  {
    id: 'local_4',
    english: 'Life is what happens to you while you\'re busy making other plans.',
    chinese: '生活就是当你忙于制定其他计划时发生在你身上的事情。',
    author: 'John Lennon',
    category: 'life',
    difficulty: 'intermediate',
    tags: ['生活', '计划', '人生']
  },
  {
    id: 'local_5',
    english: 'The way to get started is to quit talking and begin doing.',
    chinese: '开始的方法就是停止空谈，开始行动。',
    author: 'Walt Disney',
    category: 'action',
    difficulty: 'beginner',
    tags: ['行动', '开始', '实践']
  },
  {
    id: 'local_6',
    english: 'Don\'t be afraid to give up the good to go for the great.',
    chinese: '不要害怕放弃好的去追求伟大的。',
    author: 'John D. Rockefeller',
    category: 'growth',
    difficulty: 'intermediate',
    tags: ['成长', '勇气', '追求']
  },
  {
    id: 'local_7',
    english: 'Innovation distinguishes between a leader and a follower.',
    chinese: '创新区分了领导者和追随者。',
    author: 'Steve Jobs',
    category: 'innovation',
    difficulty: 'intermediate',
    tags: ['创新', '领导', '区别']
  },
  {
    id: 'local_8',
    english: 'The only impossible journey is the one you never begin.',
    chinese: '唯一不可能的旅程是你从未开始的旅程。',
    author: 'Tony Robbins',
    category: 'motivation',
    difficulty: 'beginner',
    tags: ['开始', '可能', '旅程']
  },
  {
    id: 'local_9',
    english: 'Your time is limited, don\'t waste it living someone else\'s life.',
    chinese: '你的时间有限，不要浪费它去过别人的生活。',
    author: 'Steve Jobs',
    category: 'life',
    difficulty: 'intermediate',
    tags: ['时间', '自己', '生活']
  },
  {
    id: 'local_10',
    english: 'The greatest glory in living lies not in never falling, but in rising every time we fall.',
    chinese: '生活中最大的荣耀不在于永不跌倒，而在于每次跌倒后都能站起来。',
    author: 'Nelson Mandela',
    category: 'resilience',
    difficulty: 'intermediate',
    tags: ['荣耀', '跌倒', '站起来']
  }
];

// 免费API端点
const freeAPIs = [
  'https://api.quotable.io/random',
  'https://zenquotes.io/api/random'
];

// 缓存键名
const CACHE_KEY = 'daily_english_cache';
const PRELOAD_CACHE_KEY = 'daily_english_preload_cache';
const CACHE_EXPIRY = 60 * 60 * 1000; // 1小时（毫秒）

/**
 * 获取缓存数据
 */
const getCachedData = (key) => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    
    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();
    
    // 检查是否过期
    if (now - timestamp > CACHE_EXPIRY) {
      localStorage.removeItem(key);
      return null;
    }
    
    return data;
  } catch (error) {
    console.warn('读取缓存失败:', error);
    return null;
  }
};

/**
 * 设置缓存数据
 */
const setCachedData = (key, data) => {
  try {
    const cacheData = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('设置缓存失败:', error);
  }
};

/**
 * 预加载下一句（后台静默加载）
 */
export const preloadNextSentence = async () => {
  try {
    // 检查是否已有预加载缓存
    const preloadCache = getCachedData(PRELOAD_CACHE_KEY);
    if (preloadCache) {
      return preloadCache;
    }

    // 后台预加载
    const sentence = await fetchSentenceWithFallback();
    if (sentence) {
      setCachedData(PRELOAD_CACHE_KEY, sentence);
    }
    return sentence;
  } catch (error) {
    // 在扩展环境中不输出错误日志
    if (!isExtensionEnvironment()) {
      console.warn('预加载失败:', error);
    }
    return null;
  }
};

/**
 * 获取句子（带降级策略）
 */
const fetchSentenceWithFallback = async () => {
  try {
    // 首先尝试从免费API获取
    const apiSentence = await fetchFromAPI();
    if (apiSentence) {
      return apiSentence;
    }
  } catch (error) {
    // 在扩展环境中不输出错误日志
    if (!isExtensionEnvironment()) {
      console.warn('API获取失败，使用本地数据:', error);
    }
  }

  // 使用本地数据作为备用
  return getLocalSentence();
};

/**
 * 获取每日英语句子
 * 优先使用缓存，然后使用预加载，最后实时获取
 */
export const getDailySentence = async () => {
  // 1. 优先使用当前缓存
  const cachedSentence = getCachedData(CACHE_KEY);
  if (cachedSentence) {
    // 异步预加载下一句（不阻塞当前返回）
    preloadNextSentence().catch(() => {});
    return cachedSentence;
  }

  // 2. 使用预加载缓存（如果存在）
  const preloadSentence = getCachedData(PRELOAD_CACHE_KEY);
  if (preloadSentence) {
    // 将预加载数据转为当前缓存
    setCachedData(CACHE_KEY, preloadSentence);
    // 清除预加载缓存
    localStorage.removeItem(PRELOAD_CACHE_KEY);
    // 异步预加载下一句
    preloadNextSentence().catch(() => {});
    return preloadSentence;
  }

  // 3. 实时获取
  const sentence = await fetchSentenceWithFallback();
  if (sentence) {
    // 保存到缓存
    setCachedData(CACHE_KEY, sentence);
    // 异步预加载下一句
    preloadNextSentence().catch(() => {});
  }
  
  return sentence;
};

/**
 * 手动刷新获取新句子
 * 优先使用预加载缓存，确保每次刷新都更换数据
 */
export const refreshDailySentence = async () => {
  // 1. 优先使用预加载缓存（如果存在）
  const preloadSentence = getCachedData(PRELOAD_CACHE_KEY);
  if (preloadSentence) {
    // 将预加载数据转为当前缓存
    setCachedData(CACHE_KEY, preloadSentence);
    // 清除预加载缓存
    localStorage.removeItem(PRELOAD_CACHE_KEY);
    // 立即预加载下一句
    preloadNextSentence().catch(() => {});
    return preloadSentence;
  }

  // 2. 如果没有预加载缓存，实时获取新数据
  const sentence = await fetchSentenceWithFallback();
  if (sentence) {
    // 保存到缓存
    setCachedData(CACHE_KEY, sentence);
    // 立即预加载下一句
    preloadNextSentence().catch(() => {});
  }
  
  return sentence;
};

/**
 * 从免费API获取句子
 */
const fetchFromAPI = async () => {
  for (const apiUrl of freeAPIs) {
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        timeout: 5000 // 5秒超时
      });

      if (!response.ok) {
        continue;
      }

      const data = await response.json();
      return await formatAPIData(data);
    } catch (error) {
      // 在扩展环境中静默处理错误，不输出到控制台（这些API请求失败不影响功能，会自动降级到本地数据）
      // 注意：浏览器可能仍会在控制台显示CORS错误，但这是浏览器级别的警告，无法完全隐藏
      if (!isExtensionEnvironment()) {
        console.warn(`API ${apiUrl} 请求失败:`, error);
      }
      // 静默继续尝试下一个API
      continue;
    }
  }
  return null;
};

/**
 * 格式化API数据
 */
const formatAPIData = async (apiData) => {
  // 处理不同的API响应格式
  let content, author, tags = [];

  if (apiData.content) {
    // quotable.io 格式
    content = apiData.content;
    author = apiData.author;
    tags = apiData.tags || [];
  } else if (apiData.q) {
    // zenquotes.io 格式
    content = apiData.q;
    author = apiData.a;
    tags = [];
  } else if (Array.isArray(apiData) && apiData.length > 0) {
    // 数组格式
    const item = apiData[0];
    content = item.content || item.q;
    author = item.author || item.a;
    tags = item.tags || [];
  }

  return {
    id: `api_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
    english: content,
    chinese: await translateText(content),
    author: author || 'Unknown',
    category: getCategoryFromTags(tags),
    difficulty: 'intermediate',
    tags: tags,
    source: 'API'
  };
};

/**
 * 获取本地句子
 */
const getLocalSentence = () => {
  const randomIndex = Math.floor(Math.random() * localSentences.length);
  const sentence = localSentences[randomIndex];
  
  return {
    ...sentence,
    source: 'Local',
    date: new Date().toISOString().split('T')[0]
  };
};

/**
 * 简单翻译（使用免费翻译API）
 */
const translateText = async (text) => {
  try {
    // 使用免费的翻译API
    const response = await fetch(
      `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|zh`,
      { timeout: 3000 }
    );
    
    if (response.ok) {
      const data = await response.json();
      return data.responseData.translatedText;
    }
  } catch (error) {
    console.warn('翻译失败:', error);
  }
  
  // 翻译失败时返回默认文本
  return '翻译加载中...';
};

/**
 * 根据标签获取分类
 */
const getCategoryFromTags = (tags) => {
  const categoryMap = {
    'motivation': 'motivation',
    'success': 'success',
    'life': 'life',
    'work': 'work',
    'love': 'love',
    'wisdom': 'wisdom',
    'inspiration': 'motivation'
  };

  for (const tag of tags) {
    if (categoryMap[tag.toLowerCase()]) {
      return categoryMap[tag.toLowerCase()];
    }
  }

  return 'wisdom'; // 默认分类
};

/**
 * 获取指定分类的句子
 */
export const getSentencesByCategory = (category) => {
  return localSentences.filter(sentence => 
    sentence.category === category
  );
};

/**
 * 获取指定难度的句子
 */
export const getSentencesByDifficulty = (difficulty) => {
  return localSentences.filter(sentence => 
    sentence.difficulty === difficulty
  );
};

/**
 * 搜索句子
 */
export const searchSentences = (keyword) => {
  const lowerKeyword = keyword.toLowerCase();
  return localSentences.filter(sentence => 
    sentence.english.toLowerCase().includes(lowerKeyword) ||
    sentence.chinese.includes(keyword) ||
    sentence.tags.some(tag => tag.includes(keyword))
  );
};
